import React, { FC, useEffect } from "react";
import { useHistory } from "react-router-dom";
import cn from "clsx";
import { ErrorBoundary } from "react-error-boundary";
import { getMetaTitleDescription } from "@/util/seo";
import { selectCommonSettings, selectDeviceType } from "@/util/selectors";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { useIsRTL, useLongLanguage, useCountryId, useLanguage } from "@/hooks";
import { getDeviceType } from "@/util";
import { AnalyticService } from "@/services";
import { HOMEPAGE_ROUTE } from "@/constants";
import { getPageApi } from "@/redux/actions/page.action";
import Page from "@/components/page";
import DynamicBannersWrapper from "@/components/dynamic/dynamicBanner";

import { withPageInitialLoad } from "@/libs/pageDataBridge";
import { MetaData } from "../productListing/types";
import { CatalogDataProps } from "./types";

import "./style.scss";

const CatalogPage: FC<{
  data: CatalogDataProps;
  meta: MetaData;
  contentSettings: { countryId: number; language: string };
  subtype: string;
}> = ({ data, meta, contentSettings }) => {
  const history = useHistory();
  const settings = useAppSelector(selectCommonSettings);
  const isRTL = useIsRTL();
  const language = useLongLanguage();
  const shortLanguage = useLanguage();
  const countryId = useCountryId();
  const deviceTypeSSR = useAppSelector(state => selectDeviceType(state));
  const dispatch = useAppDispatch();
  const { title: metaTitle, description } = getMetaTitleDescription(
    meta,
    history,
    settings
  );

  useEffect(() => {
    const deviceType = deviceTypeSSR || getDeviceType();
    const hasContent = data.content?.length > 0;
    const deviceTypeMissing = hasContent && !data.content[0][deviceType];
    const countryMismatch =
      contentSettings?.countryId && contentSettings.countryId !== countryId;
    const languageMismatch =
      contentSettings?.language && contentSettings.language !== shortLanguage;
    const needToRefetchApi =
      deviceTypeMissing || countryMismatch || languageMismatch;

    AnalyticService.common.trackViewCategory(data.title);

    if (needToRefetchApi) {
      dispatch(getPageApi({ url: HOMEPAGE_ROUTE, language, countryId }));
    }
  }, []);

  return (
    <Page description={description} id="catalog" indexable title={metaTitle}>
      <div
        className={cn("catalog", {
          catalog_arabic: isRTL
        })}
      >
        {data.content.map((catalogItem, index) => {
          return (
            <ErrorBoundary fallback={null} key={index}>
              <DynamicBannersWrapper
                section={catalogItem}
                bannerPageTitle={data.title}
              />
            </ErrorBoundary>
          );
        })}
      </div>
    </Page>
  );
};

export default withPageInitialLoad({
  pageName: props => {
    if (props.subtype === "home") {
      setTimeout(() => AnalyticService.common.trackHomePageEvent(), 0);
    }
    return cn({ [props.subtype]: props.subtype }, "catalog page");
  },
  page: CatalogPage,
  ssr: true,
  loader: false
});
